import { PERMISSION_SNAPSHOT_KEY } from '../components/DynamicMenu.tsx';

export const PERMISSION_SNAPSHOT_UPDATED_EVENT = 'permissionSnapshotUpdated';

export interface PermissionSnapshot {
  roleId?: string | number | null;
  menuIds: Array<string | number>;
  actions: string[];
  scopedActions: string[];
  timestamp?: number;
}

const normalize = (value?: string | null): string => String(value || '').trim().toLowerCase();
const normalizeActionCode = (value?: string | null): string => normalize(value).replace(/^\/+/, '');
const normalizeScopedCode = (value?: string | null): string => {
  const raw = normalize(value);
  if (!raw) return '';

  const idx = raw.lastIndexOf(':');
  if (idx === -1) return normalizeActionCode(raw);

  const menuKey = raw.slice(0, idx).replace(/^\/+|\/+$/g, '');
  const actionCode = normalizeActionCode(raw.slice(idx + 1));
  return menuKey && actionCode ? `${menuKey}:${actionCode}` : '';
};

const normalizeMenuKey = (menu?: string): string => normalize(menu).replace(/^\/+|\/+$/g, '');

export const toScopedActionCode = (menu: string, action: string): string => {
  const menuKey = normalizeMenuKey(menu);
  const actionCode = normalizeActionCode(action);
  return menuKey && actionCode ? `${menuKey}:${actionCode}` : '';
};

export const loadPermissionSnapshot = (): PermissionSnapshot => {
  try {
    const raw = localStorage.getItem(PERMISSION_SNAPSHOT_KEY);
    if (!raw) {
      return { menuIds: [], actions: [], scopedActions: [] };
    }
    const parsed = JSON.parse(raw);
    return {
      roleId: parsed?.roleId ?? null,
      menuIds: Array.isArray(parsed?.menuIds) ? parsed.menuIds : [],
      actions: Array.isArray(parsed?.actions) ? parsed.actions.map((x: any) => normalizeActionCode(String(x))).filter(Boolean) : [],
      scopedActions: Array.isArray(parsed?.scopedActions)
        ? parsed.scopedActions.map((x: any) => normalizeScopedCode(String(x))).filter(Boolean)
        : [],
      timestamp: parsed?.timestamp,
    };
  } catch (e) {
    return { menuIds: [], actions: [], scopedActions: [] };
  }
};

export const hasActionPermission = (
  snapshot: PermissionSnapshot,
  action: string,
  options?: {
    menu?: string;
    routePath?: string;
    allowGlobalFallback?: boolean;
  }
): boolean => {
  const normalizedAction = normalizeActionCode(action);
  if (!normalizedAction) return false;

  const actionSet = new Set((snapshot.actions || []).map(normalizeActionCode));
  const scopedSet = new Set((snapshot.scopedActions || []).map(normalizeScopedCode));

  const menuCandidates = new Set<string>();

  if (options?.menu) {
    menuCandidates.add(normalizeMenuKey(options.menu));
  }

  const routePath = options?.routePath || '';
  if (routePath) {
    const firstSegment = normalizeMenuKey(routePath.split('?')[0].split('#')[0]).split('/').filter(Boolean)[0];
    if (firstSegment) {
      menuCandidates.add(firstSegment);
    }
  }

  for (const menuKey of menuCandidates) {
    const scopedCode = toScopedActionCode(menuKey, normalizedAction);
    if (scopedCode && scopedSet.has(scopedCode)) {
      return true;
    }
  }

  if (options?.allowGlobalFallback !== false) {
    if (actionSet.has(normalizedAction)) {
      return true;
    }
  }

  return false;
};
